import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
} from "recharts";
import { Activity, Cpu, CheckCircle2, Clock, Zap, Server, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";

const tooltipStyle = { background: "#0E1019", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 };

export default function Operations() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/operations/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Running Jobs", value: stats.running, icon: Zap, color: "text-cyan-400" },
        { label: "Queued", value: stats.queued, icon: Clock, color: "text-amber-400" },
        { label: "Completed Today", value: stats.completed_today?.toLocaleString(), icon: CheckCircle2, color: "text-emerald-400" },
        { label: "Render Engines", value: stats.gpu_nodes_online, icon: Server, color: "text-purple-400" },
      ]
    : [];

  return (
    <div data-testid="operations-center-dashboard" className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <Header
        icon={Activity}
        eyebrow="Operations Center · Live"
        title="System Command"
        subtitle="Real-time telemetry across pipeline jobs, GPU clusters and engine health."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            data-testid={`ops-card-${i}`}
            className="glass rounded-2xl p-5"
          >
            <c.icon size={20} className={`${c.color} mb-3`} />
            <div className="text-2xl font-display font-extrabold">{c.value ?? "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{c.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="font-display font-semibold text-sm">GPU Cluster Throughput · 24h</span>
            <span className="text-[10px] font-mono text-emerald-400">{stats?.uptime}</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats?.throughput || []}>
              <defs>
                <linearGradient id="gGpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gJobs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="time" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="gpu" stroke="#8B5CF6" fill="url(#gGpu)" strokeWidth={2} />
              <Area type="monotone" dataKey="jobs" stroke="#06B6D4" fill="url(#gJobs)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-5">
          <span className="font-display font-semibold text-sm">System Health Radar</span>
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={stats?.health_radar || []}>
              <PolarGrid stroke="rgba(255,255,255,0.1)" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: "#94A3B8", fontSize: 10 }} />
              <Radar dataKey="value" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Cpu size={15} className="text-cyan-400" />
          <span className="font-display font-semibold text-sm">Pipeline Stream · Recent Jobs</span>
          {stats && (
            <span className="ml-auto flex items-center gap-1 text-[10px] font-mono text-slate-500">
              <AlertTriangle size={12} className="text-amber-400" /> err {stats.error_rate}
            </span>
          )}
        </div>
        <div className="space-y-2 font-mono text-xs">
          {(stats?.recent_jobs || []).length === 0 && (
            <div className="text-slate-600 py-6 text-center">No jobs yet — run a pipeline in the Studio.</div>
          )}
          {(stats?.recent_jobs || []).map((j, i) => (
            <div key={i} data-testid={`job-row-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
              <span className="text-emerald-400">●</span>
              <span className="text-purple-300">{j.job_id}</span>
              <span className="text-slate-400 truncate flex-1">{j.title || j.prompt}</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-300">{j.mode}</span>
              <span className="text-emerald-400">{j.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Header({ icon: Icon, eyebrow, title, subtitle }) {
  return (
    <div className="mb-7 float-in">
      <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-purple-400 mb-2">
        <Icon size={14} /> {eyebrow}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
      <p className="text-slate-400 mt-2 text-sm sm:text-base max-w-2xl">{subtitle}</p>
    </div>
  );
}
